/**
 * usePreventNativeKeyboardScroll
 *
 * Prevents WKWebView's native scroll-to-active-element behavior.
 *
 * When an input is focused in iOS WKWebView (including Telegram Mini Apps),
 * the native UIKit layer scrolls the entire web view to center the input
 * above the keyboard. This happens on the native UIScrollView wrapping the
 * web content — outside CSS/JS control. `overflow: hidden` and
 * `position: fixed` on html/body do NOT prevent it.
 *
 * Fix: Force `window.scrollTo(0, 0)` on every scroll event and after focus.
 * Our app uses `position: fixed` on the root layout, so the document should
 * never scroll. Any document scroll is WKWebView's keyboard accommodation.
 */

import { useEffect } from 'react';

export function usePreventNativeKeyboardScroll(): void {
  useEffect(() => {
    let rafId = 0;

    // Force document scroll back to zero — the native scroll is the only
    // thing that can move it since our layout is position:fixed.
    const resetScroll = () => {
      if (window.scrollY !== 0 || window.scrollX !== 0) {
        window.scrollTo(0, 0);
      }
    };

    // On any window scroll, immediately correct it
    const onScroll = () => {
      resetScroll();
      // Also schedule a rAF reset — sometimes WKWebView applies the scroll
      // across multiple frames
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(resetScroll);
    };

    // On input focus, reset after a short delay (native scroll fires async)
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (!target?.matches?.('input, textarea, select, [contenteditable]')) return;

      resetScroll();
      // The native scroll can fire slightly after the focus event
      setTimeout(resetScroll, 50);
      setTimeout(resetScroll, 150);
    };

    // On blur, do a final reset after keyboard dismisses
    const onFocusOut = () => {
      setTimeout(resetScroll, 300);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);

    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      cancelAnimationFrame(rafId);
    };
  }, []);
}
